import React from 'react';
import { Phone, MessageCircle, Star, Shield, Clock, Award } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo } from '../data/mock';

const HeroSection = () => {
  return (
    <section id="accueil" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #D4AF37 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
      
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Trust Badge */}
        <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6 sm:mb-10">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 fill-amber-500" />
            ))}
          </div>
          <span className="text-amber-500 text-xs sm:text-base font-semibold">Plus de 290 clients satisfaits dans le monde</span>
        </div>

        {/* Main Heading - Magnifique */}
        <div className="mb-8 sm:mb-10 px-2">
          <h1 className="leading-none tracking-tight">
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-100 to-gray-300 drop-shadow-2xl whitespace-nowrap">
              Retour Affectif
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 mt-2 sm:mt-4 drop-shadow-lg">
              Rapide & Puissant
            </span>
          </h1>
          <div className="flex items-center justify-center mt-6 sm:mt-8">
            <div className="h-[2px] w-12 sm:w-24 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
            <span className="mx-3 sm:mx-6 text-lg sm:text-2xl md:text-3xl font-bold text-white">
              Récupérer son Ex en <span className="text-amber-500">72h</span>
            </span>
            <div className="h-[2px] w-12 sm:w-24 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
          </div>
        </div>

        {/* Subtitle optimisé SEO */}
        <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-6 sm:mb-10 leading-relaxed px-2">
          <strong className="text-amber-500">Maître Diamma</strong>, grand marabout africain spécialiste du retour affectif, vous aide à 
          <strong className="text-white"> faire revenir votre ex</strong> et retrouver l'amour. 
          <strong className="text-amber-500"> Résultats garantis en 24 à 72 heures</strong>. Magie blanche, noire et rouge.
        </p>

        {/* Services Keywords optimisés SEO */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 px-2">
          {['Retour de l\'être aimé', 'Reconquérir son ex', 'Envoûtement amoureux', 'Marabout sérieux'].map((service) => (
            <span key={service} className="px-3 sm:px-5 py-2 sm:py-3 bg-gray-900/80 border border-amber-500/20 rounded-full text-xs sm:text-base text-gray-200 font-medium">
              {service}
            </span>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-10 sm:mb-16 px-4">
          <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white text-base sm:text-xl px-6 sm:px-10 py-6 sm:py-8 rounded-full shadow-lg shadow-green-600/30 transition-all duration-300 hover:scale-105 font-semibold">
              <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
              Consultation WhatsApp
            </Button>
          </a>
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black text-base sm:text-xl px-6 sm:px-10 py-6 sm:py-8 rounded-full transition-all duration-300 hover:scale-105 font-semibold">
              <Phone className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
              {contactInfo.phone}
            </Button>
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 max-w-5xl mx-auto px-2">
          {[
            { icon: Shield, text: 'Discrétion Absolue' },
            { icon: Clock, text: 'Résultats 24-72h' },
            { icon: Award, text: '30 Ans d\'Expérience' },
            { icon: Star, text: 'N°1 Retour Affectif' },
          ].map((item, index) => (
            <div key={index} className="flex flex-col items-center space-y-2 sm:space-y-3 p-4 sm:p-6 bg-gray-900/50 rounded-xl sm:rounded-2xl border border-amber-500/10 hover:border-amber-500/30 transition-all duration-300">
              <item.icon className="w-7 h-7 sm:w-10 sm:h-10 text-amber-500" />
              <span className="text-gray-200 text-xs sm:text-base font-semibold text-center">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-7 h-12 border-2 border-amber-500/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-amber-500 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
